import { useCallback, useContext, useMemo, useState } from "react";
import TabPanel from '../../common/components/nav/TabPanel';
import TabContent from '../components/nav/TabContent';
import { FormStructure as fs } from "../consts/formStructure";
import FootprintCategoryTab from '../pages/calculator-pages/FootprintCategoryTab';
import ResultTab from '../pages/calculator-pages/ResultTab';
import { OverallDataContext, TabContext } from '../pages/CalculatorPage';

export function useTabPanels() {
    const [selectedFootprintCategories, setFootprintCategories] = useContext(OverallDataContext).selectedFootprintCategories;
    const calcStage = useContext(OverallDataContext).calcStage[0];
    const inputData = useContext(OverallDataContext).inputData[0];
    const tabValue = useContext(TabContext).tabValue[0];
    
    // Tab index for custom footprint category page
    const [fpcatTabValue, setFPCTabValue] = useState(null);

     // Handle category selection
    const handleCategoryChange = useCallback((event, categoryName) => {
        setFootprintCategories(prev => ({ 
            ...prev, 
            [categoryName]: event.target.checked 
        }));
    }, []);

    const pageIndicesPerCategory = useMemo(() => {
        const visibilityMap = {};
    
        Object.entries(fs).forEach(([category, categoryContent]) => {
            if (category === "global" || (calcStage !== 1 && selectedFootprintCategories[category])) {
                categoryContent.sections.forEach((section, sectionIndex) => {
                    const hasVisibleFields = section.subsections?.some(sub =>
                    sub.fields?.some(field => {
                        if (!field.dependentOn) return true;
                        const [depField, depValue] = field.dependentOn.split(":");
                        return inputData?.[category]?.[depField] === depValue;
                    })
                    );
                    if (hasVisibleFields) {
                        if (!visibilityMap[category]) visibilityMap[category] = [];
                        visibilityMap[category].push(sectionIndex);
                    }
                });
            }
        });
        return visibilityMap;
    }, [inputData, selectedFootprintCategories]);

    function mapSectionHeadersToPages(allSections, visibleIndices, startPageNum) {
        const sectionPageMap = {};
        let sectionIndex = 0;

        allSections.forEach((sect, iterIndex) => {
            if (visibleIndices?.includes(iterIndex) && sect?.icon) {
                sectionPageMap[sectionIndex++] = startPageNum + iterIndex;
            }
        });

        return sectionPageMap;
    }

    // Memoized tab panels
    // + Sync tabs whenever selectedFootprintCategories changes
    const {tabPanels, tabNames, tagToTab} = useMemo(() => {
        let indexCounter = 1;
        const panels = [];
        const names = { 0: "Guide  1 / 1"}; // Default page 0 already provided
        const tags = {}; // Only for O T H tabs

        Object.entries(fs).forEach(([category, categoryContent]) => {
            let sectionCounter = 1;
            const sectionPageMap = mapSectionHeadersToPages(categoryContent.sections, pageIndicesPerCategory[category], indexCounter);

            categoryContent.sections.forEach((section, sectionIndex) => {
                const isVisible = pageIndicesPerCategory[category]?.includes(sectionIndex);
                if (!isVisible) return; // skip hidden sections

                let pageNum = `${sectionCounter} / ${pageIndicesPerCategory[category]?.length}`
                let sectionName = `${categoryContent.tabTitle} ${pageNum}`;
                
                panels.push(
                    <TabPanel key={`${category}-${sectionIndex}`} value={tabValue} index={indexCounter}>
                        <TabContent category={category} sect={section} sectionPageMap={sectionPageMap} pageNum={pageNum} />
                        { (process.env.NODE_ENV === 'development') &&
                            <span>tab index: {section.tag}, {indexCounter}</span>
                        }
                    </TabPanel>
                );

                tags[section.tag] = indexCounter;
                names[indexCounter] = sectionName;
                indexCounter++;
                sectionCounter++;
            });
    
            // Insert custom component immediately after "global" sections
            if (category === "global") {
                setFPCTabValue(indexCounter);
                names[indexCounter] = `Select Categories  1 / 1`;
                panels.push(
                    <TabPanel key="fpcat-custom" value={tabValue} index={indexCounter++}>
                    <TabContent>
                        <FootprintCategoryTab
                            selectedFootprintCategories={selectedFootprintCategories}
                            handleCategoryChange={handleCategoryChange}
                            showInfo={calcStage !== 1}
                        />
                    </TabContent>
                    </TabPanel>
                );
            }
        });

        // Insert result tab(s) at the end
        if (calcStage === 3) {
            names[indexCounter] = `Results  1 / 1`;
            panels.push(
                <TabPanel key="result-custom" value={tabValue} index={indexCounter++}>
                    <ResultTab 
                    
                    />
                </TabPanel>
            );
        }
        
        return {
            tabPanels: panels, 
            tabNames: names,
            tagToTab: tags,
        }
    }, [pageIndicesPerCategory, calcStage, tabValue, selectedFootprintCategories]);

    return {
        tabPanels,
        tabNames,
        tagToTab,
        fpcatTabValue,
        selectedFootprintCategories
    };
}
